import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PlayCircle, ChevronRight, X, Award } from "lucide-react";
import { DEMO_ROUTES, PORTAL_DEMO_ROUTES } from "@/data/guideContent";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// One-tap end-to-end walkthrough (great for live demos / staff training days).
// `variant="public"` walks the public platform; `variant="portal"` walks the staff portal.
export function DemoTour({ variant = "public" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPortal = variant === "portal";
  const routes = isPortal ? PORTAL_DEMO_ROUTES : DEMO_ROUTES;
  const startEvent = isPortal ? "pula-start-demo-portal" : "pula-start-demo";
  const label = isPortal ? "Staff training tour" : "Platform tour";
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [certified, setCertified] = useState(null); // {name, date}

  useEffect(() => {
    const start = () => { setI(0); setActive(true); };
    window.addEventListener(startEvent, start);
    return () => window.removeEventListener(startEvent, start);
  }, [startEvent]);

  useEffect(() => {
    if (!active) return;
    navigate(routes[i].path);
    const t = setTimeout(() => window.dispatchEvent(new CustomEvent("pula-open-guide")), 800);
    return () => clearTimeout(t);
  }, [active, i, navigate, routes]);

  const finish = useCallback(async () => {
    setActive(false);
    if (isPortal) {
      try { await api.post("/guide/training/complete"); } catch {}
      setCertified({ name: user?.name || "Team member", date: new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) });
    }
  }, [isPortal, user]);

  const next = useCallback(() => {
    if (i < routes.length - 1) setI((n) => n + 1);
    else finish();
  }, [i, routes.length, finish]);

  return (
    <>
      {active && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[65] w-[94vw] max-w-lg" data-testid="demo-tour-bar">
          <div className="bg-pula-ink text-pula-bg rounded-2xl shadow-2xl px-4 py-2.5 flex items-center gap-3">
            <PlayCircle className="w-5 h-5 text-pula-green shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-pula-bg/60 font-body leading-none">{label} · {i + 1} / {routes.length}</p>
              <p className="text-sm font-heading font-bold truncate">{routes[i].label}</p>
            </div>
            <button data-testid="demo-tour-next" onClick={next}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-pula-green text-white text-xs font-heading font-bold hover:bg-pula-greenhover whitespace-nowrap">
              {i === routes.length - 1 ? "Finish" : "Next page"} <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button data-testid="demo-tour-end" onClick={() => setActive(false)}
              className="text-pula-bg/70 hover:text-white shrink-0" aria-label="End tour"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Training completion certificate */}
      {certified && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="training-certificate">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCertified(null)} />
          <div className="relative w-full max-w-md bg-pula-bg rounded-3xl shadow-2xl overflow-hidden border-4 border-pula-green/30">
            <div className="bg-pula-green/10 px-6 py-6 text-center">
              <div className="w-16 h-16 rounded-full bg-pula-green text-white grid place-items-center mx-auto shadow-lg">
                <Award className="w-8 h-8" />
              </div>
              <p className="mt-3 text-xs tracking-widest font-heading font-bold text-pula-green uppercase">Certificate of Completion</p>
              <h2 className="mt-1 font-heading font-extrabold text-2xl text-pula-ink">Staff Training Tour</h2>
            </div>
            <div className="px-6 py-5 text-center">
              <p className="text-sm text-pula-inksoft font-body">This certifies that</p>
              <p className="text-xl font-heading font-extrabold text-pula-ink my-1">{certified.name}</p>
              <p className="text-sm text-pula-inksoft font-body">has completed the Pula &amp; Mavula staff training tour, covering Clients, Cases, Safety Concerns and Resources.</p>
              <p className="mt-3 text-xs text-pula-inksoft font-body">Completed on {certified.date}</p>
              <button data-testid="training-certificate-close" onClick={() => setCertified(null)}
                className="mt-5 w-full px-4 py-2.5 rounded-full bg-pula-green text-white text-sm font-heading font-bold hover:bg-pula-greenhover">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
